import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { HiOutlineMenuAlt3, HiOutlineX } from 'react-icons/hi';
import { StyleSheet, css } from 'aphrodite';

import images from '../../constants/images';

const menuNames = ['Home', 'Menu', 'Gallery', 'About', 'Contact', 'Vacancies'];
const getRoute = (name) => (name === 'Home' ? '/' : `/${name.toLowerCase()}`);

const Navbar = () => {
  const [toggleMenu, setToggleMenu] = useState(false);
  const [closingMenu, setClosingMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = toggleMenu ? 'hidden' : 'auto';
    return () => { document.body.style.overflow = 'auto'; };
  }, [toggleMenu]);

  const openMenu = () => {
    setClosingMenu(false);
    setToggleMenu(true);
  };

  const closeMenu = () => {
    setClosingMenu(true);
    window.setTimeout(() => {
      setToggleMenu(false);
      setClosingMenu(false);
    }, 420);
  };

  return (
    <nav className={css(styles.navbar, scrolled && styles.scrolled)} aria-label="Main navigation">
      <Link to="/" className={css(styles.logo)} aria-label="Fortress Restaurant home">
        <img src={images.logo} alt="Fortress Restaurant" className={css(styles.logoImage)} />
      </Link>

      <ul className={css(styles.links)}>
        {menuNames.map((item) => {
          const route = getRoute(item);
          return (
            <li key={item}>
              <Link to={route} className={css(styles.link, pathname === route && styles.activeLink)}>{item}</Link>
            </li>
          );
        })}
      </ul>

      <Link to="/reservation" className={css(styles.reserve)}>Reserve a table</Link>

      <div className={css(styles.mobileNav)}>
        <Link to="/" aria-label="Fortress Restaurant home">
          <img src={images.logo} alt="Fortress Restaurant" className={css(styles.mobileLogo)} />
        </Link>
        <button className={css(styles.iconButton)} onClick={openMenu} aria-label="Open menu"><HiOutlineMenuAlt3 /></button>
      </div>

      {toggleMenu && createPortal(
        <div className={css(styles.overlay, closingMenu && styles.overlayClosing)}>
          <button className={css(styles.close, closingMenu && styles.closeClosing)} onClick={closeMenu} aria-label="Close menu"><HiOutlineX /></button>
          <div className={css(styles.overlayHeader, styles.revealItem, closingMenu && styles.itemClosing)} style={{ animationDelay: '.04s' }}>
            <img src={images.logo} alt="Fortress Restaurant" className={css(styles.overlayLogo)} />
            <span className={css(styles.menuLabel)}>Eat · Sip · Gather</span>
          </div>
          <ul className={css(styles.overlayLinks)}>
            {menuNames.map((item, index) => {
              const route = getRoute(item);
              const isActive = pathname === route;
              return (
              <li key={item} className={css(styles.revealItem, closingMenu && styles.itemClosing)} style={{ animationDelay: `${0.08 + index * 0.055}s` }}>
                <Link to={route} onClick={closeMenu} className={css(styles.overlayLink, isActive && styles.overlayLinkActive)}>
                  <span className={css(styles.linkNumber)}>0{index + 1}</span>
                  <span>{item}</span>
                  <span className={css(styles.linkArrow)} aria-hidden="true">↗</span>
                </Link>
              </li>
            )})}
          </ul>
          <div className={css(styles.overlayFooter, styles.revealItem, closingMenu && styles.itemClosing)} style={{ animationDelay: '.42s' }}>
            <p className={css(styles.reserveEyebrow)}>Come hungry. Leave happy.</p>
            <Link to="/reservation" onClick={closeMenu} className={css(styles.mobileReserve)}>Reserve a table <span aria-hidden="true">→</span></Link>
          </div>
        </div>,
        document.body
      )}
    </nav>
  );
};

const styles = StyleSheet.create({
  navbar: { width: '100%', display: 'flex', alignItems: 'center', gap: '2rem', padding: '1rem clamp(1.2rem, 4vw, 4rem)', position: 'fixed', top: 0, zIndex: 1000, background: 'rgba(246,241,231,.92)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(35,79,64,.08)', transition: '.3s ease' },
  scrolled: { background: 'rgba(255,253,247,.97)', boxShadow: '0 12px 40px rgba(23,60,50,.08)' },
  logo: { flexShrink: 0, '@media (max-width:900px)': { display: 'none' } },
  logoImage: { width: '145px', display: 'block', filter: 'brightness(.47) sepia(.35) hue-rotate(95deg) saturate(1.45)' },
  links: { display: 'flex', alignItems: 'center', gap: '1.5rem', listStyle: 'none', marginLeft: 'auto', '@media (max-width:900px)': { display: 'none' } },
  link: { color: 'var(--color-green)', fontFamily: 'var(--font-ui)', fontSize: '.7rem', fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', padding: '.4rem 0', borderBottom: '1px solid transparent', transition: '.2s ease', ':hover': { color: 'var(--color-golden)' } },
  activeLink: { borderBottomColor: 'var(--color-green)' },
  reserve: { padding: '.8rem 1.2rem', borderRadius: '999px', background: 'var(--color-green)', color: 'var(--color-white)', fontFamily: 'var(--font-ui)', fontSize: '.67rem', fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', whiteSpace: 'nowrap', '@media (max-width:900px)': { display: 'none' } },
  mobileNav: { display: 'none', '@media (max-width:900px)': { display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' } },
  mobileLogo: { width: '132px', display: 'block', filter: 'brightness(.47) sepia(.35) hue-rotate(95deg) saturate(1.45)' },
  iconButton: { border: 0, background: 'transparent', color: 'var(--color-green)', fontSize: '30px', cursor: 'pointer', display: 'grid', placeItems: 'center' },
  overlay: { position: 'fixed', inset: 0, background: 'radial-gradient(circle at 110% 100%, rgba(191,145,73,.16), transparent 34%), #173f34', color: 'var(--color-cream)', display: 'flex', flexDirection: 'column', zIndex: 2000, padding: '1.4rem clamp(1.35rem,6vw,2.5rem) 1.5rem', overflowY: 'auto', overscrollBehavior: 'contain', animationName: { from: { opacity: 0, clipPath: 'circle(0% at calc(100% - 2.7rem) 2.7rem)' }, to: { opacity: 1, clipPath: 'circle(150% at calc(100% - 2.7rem) 2.7rem)' } }, animationDuration: '.65s', animationTimingFunction: 'cubic-bezier(.22,1,.36,1)', animationFillMode: 'both', ':before': { content: '""', position: 'absolute', width: '18rem', height: '18rem', right: '-8rem', bottom: '-8rem', border: '1px solid rgba(203,166,100,.2)', borderRadius: '50%', pointerEvents: 'none' } },
  overlayClosing: { animationName: { from: { opacity: 1, clipPath: 'circle(150% at calc(100% - 2.7rem) 2.7rem)' }, to: { opacity: 0, clipPath: 'circle(0% at calc(100% - 2.7rem) 2.7rem)' } }, animationDuration: '.42s', animationTimingFunction: 'cubic-bezier(.55,0,1,.45)' },
  close: { position: 'fixed', top: '1.15rem', right: '1.15rem', width: '44px', height: '44px', margin: 0, padding: 0, display: 'grid', placeItems: 'center', border: '1px solid rgba(246,241,231,.25)', borderRadius: '50%', background: 'rgba(255,255,255,.04)', color: 'var(--color-cream)', fontSize: '28px', zIndex: 1, animationName: { from: { opacity: 0, transform: 'rotate(-70deg) scale(.7)' }, to: { opacity: 1, transform: 'rotate(0) scale(1)' } }, animationDuration: '.45s', animationDelay: '.15s', animationFillMode: 'both' },
  closeClosing: { animationName: { from: { opacity: 1, transform: 'rotate(0) scale(1)' }, to: { opacity: 0, transform: 'rotate(70deg) scale(.7)' } }, animationDuration: '.25s', animationDelay: '0s' },
  revealItem: { animationName: { from: { transform: 'translateY(24px)', filter: 'blur(5px)' }, to: { transform: 'translateY(0)', filter: 'blur(0)' } }, animationDuration: '.55s', animationTimingFunction: 'cubic-bezier(.22,1,.36,1)', animationFillMode: 'both' },
  itemClosing: { animationName: { from: { opacity: 1, transform: 'translateY(0)' }, to: { opacity: 0, transform: 'translateY(-12px)' } }, animationDuration: '.2s', animationDelay: '0s !important' },
  overlayHeader: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', paddingRight: '4rem', paddingBottom: '1.1rem', borderBottom: '1px solid rgba(246,241,231,.16)' },
  overlayLogo: { width: '128px', filter: 'brightness(0) invert(1)', opacity: .96 },
  menuLabel: { fontFamily: 'var(--font-ui)', color: 'var(--color-golden)', fontSize: '.61rem', fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase' },
  overlayLinks: { flex: 1, listStyle: 'none', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '.8rem 0', fontFamily: 'var(--font-base)' },
  overlayLink: { width: '100%', minHeight: '52px', display: 'grid', gridTemplateColumns: '2rem 1fr 2rem', alignItems: 'center', color: 'rgba(246,241,231,.76)', borderBottom: '1px solid rgba(246,241,231,.1)', fontSize: 'clamp(1.55rem,7vw,2.2rem)', lineHeight: 1, textAlign: 'center', transition: 'color .25s ease, transform .25s ease', ':hover': { color: '#fff', transform: 'translateY(-1px)' } },
  overlayLinkActive: { color: 'var(--color-golden)' },
  linkNumber: { fontFamily: 'var(--font-ui)', fontSize: '.55rem', letterSpacing: '.08em', color: 'rgba(246,241,231,.38)', alignSelf: 'center' },
  linkArrow: { fontFamily: 'var(--font-ui)', fontSize: '.85rem', opacity: .35, justifySelf: 'end' },
  overlayFooter: { flexShrink: 0, borderRadius: '18px', background: 'linear-gradient(135deg, #f6eddd, #ead8bb)', color: 'var(--color-green)', padding: '1rem 1.1rem', position: 'relative', zIndex: 1, boxShadow: '0 16px 36px rgba(4,24,18,.2)' },
  reserveEyebrow: { fontFamily: 'var(--font-ui)', fontSize: '.58rem', fontWeight: 700, letterSpacing: '.13em', textTransform: 'uppercase', color: '#96703c', marginBottom: '.45rem' },
  mobileReserve: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'var(--color-green)', fontFamily: 'var(--font-base)', fontSize: '1.35rem', fontWeight: 600 },
});

export default Navbar;
